import { DialogComponent } from '@theme/dialog';
import { formatMoney } from '@theme/money-formatting';

/**
 * ════════════════════════════════════════════════════════════════════════
 * `<bundle-details-drawer>` — the "See Details" product drawer.
 * ────────────────────────────────────────────────────────────────────────
 * Extends the theme `DialogComponent` (inherits the native <dialog>
 * open/close, scroll-lock + focus handling — requires `ref="dialog"`).
 *
 * Listens on the document for the bubbling `bundle:details` event the
 * orchestrator dispatches from `openDetails()`. `detail.product` is the
 * resolved step selection: { handle, title, price, compareAt, image,
 * images[], color, available, ... }. We render that immediately (no network
 * wait for the basics) and lazily enrich with the product's full description
 * + Judge.me star summary fetched from `/products/{handle}` — cached per
 * handle so re-opening the same product is instant.
 *
 * The Judge.me REVIEW LIST inside the drawer is owned by `<bundle-reviews>`
 * (assets/bb-reviews.js); opening the drawer dispatches `bb-drawer:open` so
 * it re-renders for the active handle.
 *
 * Owns ONLY `<bundle-details-drawer>`. Refs: dialog, image, thumbs, badge,
 * spinner, title, rating, stars, ratingCount, price, compare, savings,
 * description, cta.
 * ════════════════════════════════════════════════════════════════════════
 */
export class BundleDetailsDrawerComponent extends DialogComponent {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('bundle:details', this.#onDetails);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('bundle:details', this.#onDetails);
  }

  /** Shop money format, read off the host builder (fallback to a sane default). */
  get #moneyFormat() {
    const builder = this.closest('bundle-builder');
    // @ts-ignore — config is defined on BundleBuilderComponent.
    return builder?.config?.moneyFormat || '${{amount}}';
  }

  /** @param {CustomEvent} event */
  #onDetails = (event) => {
    const product = event.detail?.product ?? null;
    this.#render(product);
    this.showDialog();
  };

  /**
   * Render the resolved product. Everything paints synchronously from the data
   * carried on the selection (gallery, pricing, accordions, rating).
   * @param {any} product
   */
  #render(product) {
    const { title, image, price, compare, savings, badge, thumbs, description, rating } = this.refs;

    if (!product) return;

    // ── Title ──────────────────────────────────────────────────────────
    if (title) title.textContent = product.title ?? '';

    // ── Gallery ────────────────────────────────────────────────────────
    const images = Array.isArray(product.images) && product.images.length ? product.images : [product.image].filter(Boolean);
    if (image) {
      image.src = images[0] ?? product.image ?? '';
      image.alt = product.title ?? '';
    }
    this.#renderThumbs(images, product.title ?? '');

    if (badge) badge.hidden = product.available === false ? true : !product.isNew;

    // ── Pricing ────────────────────────────────────────────────────────
    this.#renderPricing(product, { price, compare, savings });

    // ── Accordions: filled directly from the metafield HTML carried on the
    //    selection (product_detail / features / specifications / why-buy).
    //    The /products/{handle}.js endpoint can't return metafields, so we
    //    don't fetch them — empty bodies hide their accordion. ─────────────
    if (description) description.innerHTML = product.detail ?? '';
    this.#fillAccordion(this.refs.featuresBody, this.refs.featuresAccordion, product.features);
    this.#fillAccordion(this.refs.specsBody, this.refs.specsAccordion, product.specs);
    this.#fillAccordion(this.refs.whyBody, this.refs.whyAccordion, product.whyBuy);

    // ── Rating: rendered instantly from the inline rating/count on the
    //    selection (same data the buy-box badge uses) — no page fetch. ──────
    this.#renderRating(product, rating);
  }

  /**
   * @param {any} product
   * @param {{ price?: any, compare?: any, savings?: any }} refs
   */
  #renderPricing(product, { price, compare, savings }) {
    const fmt = this.#moneyFormat;
    const cents = Number(product.price) || 0;
    const compareCents = Number(product.compareAt) || 0;
    if (price) price.textContent = formatMoney(cents, fmt);

    const onSale = compareCents > cents;
    if (compare) {
      compare.hidden = !onSale;
      compare.textContent = onSale ? formatMoney(compareCents, fmt) : '';
    }
    if (savings) {
      const pct = onSale ? Math.round(((compareCents - cents) / compareCents) * 100) : 0;
      savings.hidden = !onSale;
      savings.textContent = onSale ? `Save ${pct}%` : '';
    }
  }

  /**
   * @param {string[]} images
   * @param {string} alt
   */
  #renderThumbs(images, alt) {
    const { thumbs, image } = this.refs;
    if (!thumbs) return;
    if (images.length < 2) {
      thumbs.replaceChildren();
      return;
    }
    const frag = document.createDocumentFragment();
    images.forEach((src, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bb-drawer__thumb';
      btn.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      const img = document.createElement('img');
      img.src = src;
      img.alt = `${alt} thumbnail ${i + 1}`;
      img.loading = 'lazy';
      img.width = 50;
      img.height = 50;
      btn.appendChild(img);
      btn.addEventListener('click', () => {
        if (image instanceof HTMLImageElement) image.src = src;
        for (const t of thumbs.querySelectorAll('.bb-drawer__thumb')) t.setAttribute('aria-current', 'false');
        btn.setAttribute('aria-current', 'true');
      });
      frag.appendChild(btn);
    });
    thumbs.replaceChildren(frag);
  }

  /**
   * Fill a drawer accordion body with metafield HTML, hiding the whole
   * <details> when there's no content for the current product.
   * @param {Element | undefined} body
   * @param {Element | undefined} accordion
   * @param {string | undefined} html
   */
  #fillAccordion(body, accordion, html) {
    const value = html ?? '';
    if (body instanceof HTMLElement) body.innerHTML = value;
    if (accordion instanceof HTMLElement) accordion.toggleAttribute('hidden', value.trim() === '');
  }

  /**
   * Render the star summary from the selection's inline rating/count using the
   * shared gold `.bb-stars` fill (matches the buy-box badge). Hidden when the
   * product has no reviews.
   * @param {{ rating?: number, reviewCount?: number } | null | undefined} product
   * @param {Element | undefined} rating
   */
  #renderRating(product, rating) {
    if (!(rating instanceof HTMLElement)) return;
    const { stars, ratingCount } = this.refs;
    const value = Number(product?.rating) || 0;
    const count = Number(product?.reviewCount) || 0;
    if (count > 0) {
      const pct = Math.max(0, Math.min(100, (value / 5) * 100));
      if (stars) {
        stars.innerHTML = `<span class="bb-stars" role="img" aria-label="${value.toFixed(1)} out of 5 stars" style="--bb-star-pct:${pct}%"></span>`;
      }
      if (ratingCount) ratingCount.textContent = `${count} review${count === 1 ? '' : 's'}`;
      rating.hidden = false;
    } else {
      rating.hidden = true;
    }
  }

}

if (!customElements.get('bundle-details-drawer')) {
  customElements.define('bundle-details-drawer', BundleDetailsDrawerComponent);
}
